import type { PolicyPriceDebug } from '../components/pricing/PolicySignalsPanel';
import type { ManapoolSalePoint } from '../components/pricing/ManapoolSalesChart';

/**
 * Policy fields on Add Inventory draft rows.
 *
 * Source of truth after reload: `GET /batches/{id}` live-computes
 * `policy_price` / `policy_price_debug` via the same helper as
 * `POST /batches/preview-pricing` (not stored on inventory_batch_items).
 *
 * If a GET payload is missing `policy_price_debug` (older API), the intake
 * panel rehydrates distinct `(mtgjson_uuid, condition, is_foil, printing)`
 * lines through `POST /batches/preview-pricing-bulk`.
 */
export interface IntakePricingFields {
  tcgplayer_market?: number | null;
  manapool_market?: number | null;
  ck_buylist?: number | null;
  tcg_export_market?: number | null;
  tcg_low?: number | null;
  manapool_low?: number | null;
  calculated_offer?: number | null;
  price_source?: string | null;
  manapool_recent_sales?: ManapoolSalePoint[] | null;
  policy_price?: number | null;
  policy_price_debug?: PolicyPriceDebug | null;
  offer_ck_floored?: boolean | null;
}

export interface IntakePreviewRequest {
  mtgjson_uuid?: string | null;
  tcgplayer_id?: string | null;
  catalog_source?: string | null;
  product_line?: string | null;
  condition: string;
  is_foil: boolean;
  printing: string;
}

export type IntakePreviewLine = IntakePreviewRequest & IntakePricingFields;

export function intakePreviewCacheKey(item: IntakePreviewRequest): string {
  const printing = (item.printing || 'normal').trim().toLowerCase() || 'normal';
  const condition = (item.condition || 'NM').trim() || 'NM';
  const identity = item.mtgjson_uuid || item.tcgplayer_id || '';
  return `${identity}|${condition}|${item.is_foil ? 1 : 0}|${printing}`;
}

export function itemNeedsPolicyRehydrate(
  item: Pick<IntakePricingFields, 'policy_price_debug'>,
): boolean {
  return item.policy_price_debug == null;
}

export function mergeIntakePricing<T extends IntakePricingFields>(
  item: T,
  preview?: IntakePricingFields | null,
): T {
  if (!preview) {
    return item;
  }
  return {
    ...item,
    tcgplayer_market: preview.tcgplayer_market ?? item.tcgplayer_market,
    manapool_market: preview.manapool_market ?? item.manapool_market,
    ck_buylist: preview.ck_buylist ?? item.ck_buylist,
    tcg_export_market: preview.tcg_export_market ?? item.tcg_export_market,
    tcg_low: preview.tcg_low ?? item.tcg_low,
    manapool_low: preview.manapool_low ?? item.manapool_low,
    calculated_offer: preview.calculated_offer ?? item.calculated_offer,
    price_source: preview.price_source ?? item.price_source,
    manapool_recent_sales:
      preview.manapool_recent_sales ?? item.manapool_recent_sales,
    policy_price: preview.policy_price ?? item.policy_price,
    policy_price_debug: preview.policy_price_debug ?? item.policy_price_debug,
    offer_ck_floored: preview.offer_ck_floored ?? item.offer_ck_floored,
  };
}

export function distinctIntakePreviewRequests(
  items: IntakePreviewRequest[],
): IntakePreviewRequest[] {
  const seen = new Set<string>();
  const out: IntakePreviewRequest[] = [];
  for (const item of items) {
    const key = intakePreviewCacheKey(item);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push({
      mtgjson_uuid: item.mtgjson_uuid,
      tcgplayer_id: item.tcgplayer_id,
      catalog_source: item.catalog_source,
      product_line: item.product_line,
      condition: item.condition,
      is_foil: item.is_foil,
      printing: item.printing,
    });
  }
  return out;
}

export function chunkArray<T>(items: T[], size: number): T[][] {
  const chunkSize = Math.max(1, size);
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
}

export async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  if (items.length === 0) {
    return [];
  }
  const limit = Math.max(1, Math.min(concurrency, items.length));
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index]);
    }
  };

  await Promise.all(Array.from({ length: limit }, () => worker()));
  return results;
}

export function indexPreviewsByKey(
  previews: IntakePreviewLine[],
): Map<string, IntakePreviewLine> {
  const map = new Map<string, IntakePreviewLine>();
  for (const preview of previews) {
    map.set(intakePreviewCacheKey(preview), preview);
  }
  return map;
}

export function mergeItemsWithPreviews<T extends IntakePreviewRequest & IntakePricingFields>(
  items: T[],
  previews: IntakePreviewLine[],
): T[] {
  const byKey = indexPreviewsByKey(previews);
  return items.map((item) =>
    mergeIntakePricing(item, byKey.get(intakePreviewCacheKey(item))),
  );
}
